
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Loader2, Save, User, Camera, Building, Users, Link as LinkIcon } from 'lucide-react';
import { internalProfileService } from '@/services/internalProfileService';
import { supabase } from '@/integrations/supabase/client';
import { photoUploadService } from '@/services/photoUploadService';
import { usePoints } from '@/hooks/usePoints';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface Team {
  team_id: number;
  team_name: string;
  slug: string;
}

const TeamOwnerProfileForm: React.FC = () => {
  const { user: supabaseUser, profile, refreshProfile } = useAuth();
  const { user: internalUser, profile: internalProfile, refreshProfile: refreshInternalProfile } = useInternalAuth();
  const { checkAndAwardProfileCompletion } = usePoints();
  const [loading, setLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [availableTeams, setAvailableTeams] = useState<Team[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    // Basic Information
    first_name: '',
    last_name: '',
    username: '',
    phone: '',
    
    // Team Owner specific fields
    team_name: '',
    team_description: '',
    team_history: '',
    team_colors: '',
    team_motto: '',
    budget_range: '',
    sponsorships: '',
    venue_selection: '',
    facilities_owned: [] as string[],
    facilities_needs: '',
    team_goals: '',
    
    // Connected teams
    connected_teams: [] as number[],
    
    // Sports preferences
    preferred_sports: [] as string[],
    
    // Profile Photo
    profile_photo: null as File | null,
    profile_photo_url: '',
    
    // Privacy settings
    show_profile_public: false,
    show_contact_public: false,
    show_team_info_public: false,
    show_budget_public: false,
  });

  // Load initial data from existing profile
  useEffect(() => {
    loadInitialData();
    loadAvailableTeams();
  }, [internalProfile, profile, internalUser]);

  const loadInitialData = () => {
    const currentProfile = internalProfile || profile;
    const currentUser = internalUser;
    
    if (currentProfile || currentUser) {
      console.log('Loading profile data:', currentProfile);
      setFormData(prev => ({
        ...prev,
        first_name: currentProfile?.first_name || '',
        last_name: currentProfile?.last_name || '',
        username: currentProfile?.username || currentUser?.username || '',
        phone: currentProfile?.phone || '',
        team_name: currentProfile?.team_name || '',
        team_description: currentProfile?.team_description || '',
        team_history: currentProfile?.team_history || '',
        team_colors: currentProfile?.team_colors || '',
        team_motto: currentProfile?.team_motto || '',
        budget_range: currentProfile?.budget_range || '',
        sponsorships: currentProfile?.sponsorships || '',
        venue_selection: currentProfile?.venue_selection || '',
        facilities_owned: currentProfile?.facilities_owned || [],
        facilities_needs: currentProfile?.facilities_needs || '',
        team_goals: currentProfile?.team_goals || '',
        connected_teams: currentProfile?.connected_teams || [],
        preferred_sports: currentProfile?.preferred_sports || [],
        profile_photo_url: currentProfile?.profile_photo_url || '',
        show_profile_public: currentProfile?.show_profile_public || false,
        show_contact_public: currentProfile?.show_contact_public || false,
        show_team_info_public: currentProfile?.show_team_info_public || false,
        show_budget_public: currentProfile?.show_budget_public || false,
      }));

      // Load profile photo preview
      if (currentProfile?.profile_photo_url) {
        loadProfilePhotoPreview(currentProfile.profile_photo_url);
      }
    }
  };

  const loadProfilePhotoPreview = async (photoPath: string) => {
    try {
      const signedUrl = await photoUploadService.getProfilePhotoUrl(photoPath);
      if (signedUrl) {
        setPhotoPreview(signedUrl);
      }
    } catch (error) {
      console.error('Error loading profile photo:', error);
    }
  };

  const loadAvailableTeams = async () => {
    setTeamsLoading(true);
    try {
      const currentUserId = internalUser?.id || supabaseUser?.id;
      if (!currentUserId) return;

      // Fetch connected_teams from user profile (same approach as TeamManagement page)
      const { data: profileData } = await supabase
        .from('internal_user_profiles')
        .select('connected_teams')
        .eq('user_id', currentUserId)
        .single();

      const connectedTeamIds = (profileData?.connected_teams as number[]) || [];

      if (connectedTeamIds.length === 0) {
        setAvailableTeams([]);
        return;
      }

      // Fetch team details for connected teams
      const { data, error } = await supabase
        .from('teams')
        .select('team_id, team_name, slug')
        .in('team_id', connectedTeamIds)
        .order('team_name');

      if (error) {
        console.error('Error loading teams:', error);
        return;
      }

      setAvailableTeams(data || []);
    } catch (error) {
      console.error('Error loading teams:', error);
    } finally {
      setTeamsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, profile_photo: file }));
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleArrayChange = (field: string, value: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked 
        ? [...(prev[field as keyof typeof prev] as string[]), value]
        : (prev[field as keyof typeof prev] as string[]).filter((item: string) => item !== value)
    }));
  };

  const handleTeamConnection = (teamId: number, connected: boolean) => {
    setFormData(prev => ({
      ...prev,
      connected_teams: connected 
        ? [...prev.connected_teams, teamId]
        : prev.connected_teams.filter(id => id !== teamId)
    }));
  };

  const uploadProfilePhoto = async (file: File): Promise<string | null> => {
    try {
      const currentUserId = internalUser?.id || supabaseUser?.id;
      if (!currentUserId) {
        console.error('No user ID available for photo upload');
        toast.error('Cannot upload photo: User not authenticated');
        return null;
      }

      console.log('Starting photo upload for user:', currentUserId);
      const result = await photoUploadService.uploadProfilePhoto(file, currentUserId);
      console.log('Photo upload result:', result);
      
      if (result.success && result.url) {
        console.log('Photo uploaded successfully, path:', result.url);
        return result.url;
      } else {
        console.error('Photo upload failed:', result.error);
        toast.error('Photo upload failed: ' + (result.error || 'Unknown error'));
        return null;
      }
    } catch (error) {
      console.error('Error uploading profile photo:', error);
      toast.error('Error uploading photo');
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    console.log('=== TEAM OWNER FORM SUBMISSION START ===');
    console.log('Form data being submitted:', formData);

    try {
      let profilePhotoUrl = formData.profile_photo_url;
      
      // Upload profile photo if one was selected
      if (formData.profile_photo) {
        console.log('Uploading profile photo...');
        const uploadedPath = await uploadProfilePhoto(formData.profile_photo);
        if (uploadedPath) {
          profilePhotoUrl = uploadedPath;
          console.log('Profile photo uploaded to:', profilePhotoUrl);
        }
      }

      // Get the current user ID
      const currentUserId = internalUser?.id || supabaseUser?.id;
      const userType = internalUser ? 'internal' : 'supabase';
      
      if (!currentUserId) {
        toast.error('User not authenticated');
        return;
      }

      console.log('Saving profile for user:', currentUserId, 'Type:', userType);

      if (internalUser) {
        // For internal users - save to internal_user_profiles table
        // Use authoritative username and role from internal_users table
        const profileData = {
          user_id: currentUserId,
          first_name: formData.first_name,
          last_name: formData.last_name,
          username: internalUser.username, // Use authoritative username from internal_users
          phone: formData.phone || null, // Ensure phone is included
          profile_photo_url: profilePhotoUrl || null,
          role: internalUser.role || 'team-owner', // Use authoritative role from internal_users
          
          // Team Owner specific fields
          team_name: formData.team_name || null,
          team_description: formData.team_description || null,
          team_history: formData.team_history || null,
          team_colors: formData.team_colors || null,
          team_motto: formData.team_motto || null,
          budget_range: formData.budget_range || null,
          sponsorships: formData.sponsorships || null,
          venue_selection: formData.venue_selection || null,
          facilities_owned: Array.isArray(formData.facilities_owned) && formData.facilities_owned.length > 0 
            ? formData.facilities_owned 
            : null,
          facilities_needs: formData.facilities_needs || null,
          team_goals: formData.team_goals || null,
          connected_teams: Array.isArray(formData.connected_teams) && formData.connected_teams.length > 0 
            ? formData.connected_teams 
            : null,
          preferred_sports: Array.isArray(formData.preferred_sports) && formData.preferred_sports.length > 0 
            ? formData.preferred_sports 
            : null,
          
          // Privacy Settings - booleans should always be set
          show_profile_public: formData.show_profile_public ?? false,
          show_contact_public: formData.show_contact_public ?? false,
          show_team_info_public: formData.show_team_info_public ?? false,
          show_budget_public: formData.show_budget_public ?? false,
        };

        console.log('=== TEAM OWNER PROFILE DATA TO SAVE ===');
        console.log('phone:', profileData.phone);
        console.log('profile_photo_url:', profileData.profile_photo_url);
        console.log('preferred_sports:', profileData.preferred_sports);
        console.log('facilities_owned:', profileData.facilities_owned);
        console.log('privacy settings:', {
          show_profile_public: profileData.show_profile_public,
          show_contact_public: profileData.show_contact_public,
          show_team_info_public: profileData.show_team_info_public,
          show_budget_public: profileData.show_budget_public,
        });

        const result = await internalProfileService.upsertProfile(profileData);
        if (!result.success) {
          console.error('Internal profile save failed:', result.error);
          throw new Error(result.error);
        }

        // Verify critical fields were saved correctly
        const { data: savedProfile, error: verifyError } = await supabase
          .from('internal_user_profiles')
          .select('profile_photo_url, phone, preferred_sports, facilities_owned, show_profile_public')
          .eq('user_id', currentUserId)
          .single();
        
        if (verifyError) {
          console.error('Error verifying saved profile:', verifyError);
        } else {
          console.log('=== VERIFICATION: Saved profile in database ===');
          console.log('phone:', savedProfile?.phone);
          console.log('profile_photo_url:', savedProfile?.profile_photo_url);
          console.log('preferred_sports:', savedProfile?.preferred_sports);
          console.log('facilities_owned:', savedProfile?.facilities_owned);
          console.log('show_profile_public:', savedProfile?.show_profile_public);
          
          // Force update if profile photo was not saved
          if (profilePhotoUrl && savedProfile?.profile_photo_url !== profilePhotoUrl) {
            console.warn('Profile photo URL mismatch! Forcing update...');
            const { error: forceError } = await supabase
              .from('internal_user_profiles')
              .update({ profile_photo_url: profilePhotoUrl })
              .eq('user_id', currentUserId);
            if (forceError) {
              console.error('Force update failed:', forceError);
            }
          }
        }

        console.log('Internal profile saved successfully');
        
        // Refresh profile data so header/menu updates immediately
        await refreshInternalProfile();
        
      } else if (supabaseUser) {
        // For Supabase users - save to profiles table
        const updateData = {
          id: currentUserId,
          email: supabaseUser.email,
          first_name: formData.first_name,
          last_name: formData.last_name,
          username: formData.username,
          phone: formData.phone,
          profile_photo_url: profilePhotoUrl,
          role: 'team-owner',
          
          // Team Owner specific fields
          team_name: formData.team_name,
          team_description: formData.team_description,
          budget_range: formData.budget_range,
          sponsorships: formData.sponsorships,
          preferred_sports: formData.preferred_sports,
          
          // Privacy Settings
          show_profile_public: formData.show_profile_public,
          show_contact_public: formData.show_contact_public,
        };

        console.log('Saving Supabase profile data:', updateData);

        const { error } = await supabase
          .from('profiles')
          .upsert(updateData, {
            onConflict: 'id'
          });

        if (error) {
          console.error('Supabase profile save failed:', error);
          throw error;
        }

        console.log('Supabase profile saved successfully');
        await refreshProfile();
      }

      // Check for profile completion and award points
      try {
        const savedProfile = internalUser ? 
          { ...formData, user_id: currentUserId, role: 'team-owner' } : 
          { ...formData, id: currentUserId, role: 'team-owner' };
        const wasAwarded = await checkAndAwardProfileCompletion(savedProfile, 'team-owner');
        if (wasAwarded) {
          toast.success('🎉 Profile Complete! +50 Points Earned!', { 
            duration: 5000,
            description: 'Your profile is now 100% complete!'
          });
        } else {
          toast.success('Team Owner profile updated successfully!');
        }
      } catch (error) {
        console.error('Error checking profile completion:', error);
        toast.success('Team Owner profile updated successfully!');
      }
      
      console.log('=== TEAM OWNER FORM SUBMISSION SUCCESS ===');
      
    } catch (error) {
      console.error('=== TEAM OWNER FORM SUBMISSION ERROR ===', error);
      toast.error('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const facilitiesOptions = [
    'Training Ground', 'Stadium', 'Gym', 'Locker Rooms', 'Medical Facility',
    'Equipment Storage', 'Meeting Rooms', 'Parking', 'Concessions'
  ];

  const sportsOptions = [
    'Football', 'Basketball', 'Soccer', 'Baseball', 'Tennis', 
    'Golf', 'Swimming', 'Track & Field', 'Volleyball', 'Hockey'
  ];

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Profile Photo Section */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Camera className="h-5 w-5" />
              Profile Photo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              <div className="w-24 h-24 bg-gray-700 rounded-full flex items-center justify-center overflow-hidden">
                {photoPreview || formData.profile_photo_url ? (
                  <img 
                    src={photoPreview || formData.profile_photo_url} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="h-12 w-12 text-gray-400" />
                )}
              </div>
              <div>
                <Label htmlFor="profile_photo" className="cursor-pointer">
                  <div className="bg-sports-green text-black px-4 py-2 rounded-lg hover:bg-green-600 transition-colors inline-flex items-center gap-2">
                    <Camera className="h-4 w-4" />
                    Upload Photo
                  </div>
                </Label>
                <Input
                  id="profile_photo"
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Basic Information */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name" className="text-gray-300">First Name *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => handleInputChange('first_name', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="last_name" className="text-gray-300">Last Name *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => handleInputChange('last_name', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="username" className="text-gray-300">Username *</Label>
                <Input
                  id="username"
                  value={formData.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-gray-300">Phone Number *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                  required
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Teams Owned Section - Read Only Display */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <LinkIcon className="h-5 w-5 text-sports-green" />
              Teams Owned
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {teamsLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : availableTeams.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-gray-400 mb-2">No teams connected yet.</p>
                <p className="text-sm text-gray-500 mb-4">
                  Connect or create a team from your dashboard to see it here.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => window.location.href = '/dashboard'}
                  className="border-sports-green text-sports-green hover:bg-sports-green hover:text-black"
                >
                  <Users className="h-4 w-4 mr-2" />
                  Go to Dashboard
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {availableTeams.map((team) => (
                  <div key={team.team_id} className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-sports-green/20 rounded-full flex items-center justify-center">
                        <Users className="h-5 w-5 text-sports-green" />
                      </div>
                      <div>
                        <span className="text-white font-medium">{team.team_name}</span>
                        <p className="text-sm text-gray-400">Team Owner</p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => window.location.href = '/team-management'}
                      className="border-gray-600 text-gray-300 hover:bg-gray-600"
                    >
                      Manage
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => window.location.href = '/dashboard'}
                  className="w-full border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  <Users className="h-4 w-4 mr-2" />
                  Connect Another Team
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Note: Team Information is managed from the Dashboard */}
        {availableTeams.length > 0 && (
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="py-4">
              <p className="text-gray-400 text-sm text-center">
                Team details (name, description, colors, etc.) are managed from Team Management. 
                <Button
                  type="button"
                  variant="link"
                  onClick={() => window.location.href = '/team-management'}
                  className="text-sports-green hover:text-green-400 px-1"
                >
                  Go to Team Management →
                </Button>
              </p>
            </CardContent>
          </Card>
        )}

        {/* Sports Preferences */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Sports Preferences</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {sportsOptions.map((sport) => (
                <div key={sport} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={sport}
                    checked={formData.preferred_sports.includes(sport)}
                    onChange={(e) => handleArrayChange('preferred_sports', sport, e.target.checked)}
                    className="w-4 h-4 text-sports-green bg-gray-700 border-gray-600 rounded focus:ring-sports-green"
                  />
                  <Label htmlFor={sport} className="text-gray-300 text-sm cursor-pointer">
                    {sport}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Business Information */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Business Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="budget_range" className="text-gray-300">Budget Range</Label>
              <Input
                id="budget_range"
                value={formData.budget_range}
                onChange={(e) => handleInputChange('budget_range', e.target.value)}
                placeholder="e.g., $10,000 - $50,000"
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            
            <div>
              <Label htmlFor="sponsorships" className="text-gray-300">Sponsorships</Label>
              <Textarea
                id="sponsorships"
                value={formData.sponsorships}
                onChange={(e) => handleInputChange('sponsorships', e.target.value)}
                placeholder="List current sponsorships and partnerships..."
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            
            <div>
              <Label htmlFor="venue_selection" className="text-gray-300">Venue Selection Criteria</Label>
              <Textarea
                id="venue_selection"
                value={formData.venue_selection}
                onChange={(e) => handleInputChange('venue_selection', e.target.value)}
                placeholder="What do you look for in venues?"
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
          </CardContent>
        </Card>

        {/* Facilities */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Facilities Owned</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {facilitiesOptions.map((facility) => (
                <div key={facility} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={facility}
                    checked={formData.facilities_owned.includes(facility)}
                    onChange={(e) => handleArrayChange('facilities_owned', facility, e.target.checked)}
                    className="w-4 h-4 text-sports-green bg-gray-700 border-gray-600 rounded focus:ring-sports-green"
                  />
                  <Label htmlFor={facility} className="text-gray-300 text-sm cursor-pointer">
                    {facility}
                  </Label>
                </div>
              ))}
            </div>
            
            <div>
              <Label htmlFor="facilities_needs" className="text-gray-300">Facility Needs</Label>
              <Textarea
                id="facilities_needs"
                value={formData.facilities_needs}
                onChange={(e) => handleInputChange('facilities_needs', e.target.value)}
                placeholder="What facilities does your team need?"
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
          </CardContent>
        </Card>

        {/* Privacy Settings */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Privacy Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
              <div>
                <Label className="text-white font-medium">Make Profile Public</Label>
                <p className="text-sm text-gray-400">Allow others to view your profile</p>
              </div>
              <Switch
                checked={formData.show_profile_public}
                onCheckedChange={(checked) => handleInputChange('show_profile_public', checked)}
              />
            </div>
            
            <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
              <div>
                <Label className="text-white font-medium">Show Contact Information</Label>
                <p className="text-sm text-gray-400">Display contact info publicly</p>
              </div>
              <Switch
                checked={formData.show_contact_public}
                onCheckedChange={(checked) => handleInputChange('show_contact_public', checked)}
              />
            </div>
            
            <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
              <div>
                <Label className="text-white font-medium">Show Team Information</Label>
                <p className="text-sm text-gray-400">Display team details publicly</p>
              </div>
              <Switch
                checked={formData.show_team_info_public}
                onCheckedChange={(checked) => handleInputChange('show_team_info_public', checked)}
              />
            </div>
            
            <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
              <div>
                <Label className="text-white font-medium">Show Budget Information</Label>
                <p className="text-sm text-gray-400">Display budget range publicly</p>
              </div>
              <Switch
                checked={formData.show_budget_public}
                onCheckedChange={(checked) => handleInputChange('show_budget_public', checked)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={loading}
            className="bg-sports-green text-black hover:bg-green-600"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Profile
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default TeamOwnerProfileForm;
